import { customGetAPIQuiet } from "services/utils/CustomAPI";
import { PtApiError, ptRequest } from "pages/PayRoll/Configuration/StatutoryConfiguration/PT/ptConfigApi";

// PT Reports (screens PT3–PT6a of the PT Frontend API Guide), base
// /api/payroll/statutory/pt/. Everything is read from finalised payroll;
// the only saves are Record payment, Reverse payment and Re-check now.
// Uses ptRequest from the PT Configuration API (token, 401 sign-out, field
// errors, 409 codes ALREADY_REVERSED / NOT_REVERSIBLE). The optional
// Idempotency-Key is not sent (CORS, same as ESI and LWF); Save buttons
// show a loading state instead.

const text = (value) => (value == null ? "" : String(value));
const upper = (value) => String(value || "").toUpperCase();
const list = (value) => (Array.isArray(value) ? value.filter((item) => item && typeof item === "object") : []);
// "571.00" -> 571; null / "" stay null.
const amount = (value) => (value === null || value === undefined || value === "" ? null : Number(value));

// ---- Shared ----

export const STATUS_TONES = {
  // Register
  DEDUCTED: "green",
  IN_LAST_MONTH: "slate",
  BELOW_SLAB: "slate",
  EXEMPT: "blue",
  YEARLY_LIMIT: "purple",
  SITE_NOT_SET_UP: "red",
  HELD: "red",
  HELD_MISSING_DATA: "red",
  PAY_CYCLE_NOT_SUPPORTED: "red",
  REFUND: "purple",
  // Payment periods
  COLLECTING: "blue",
  NOT_DUE_YET: "slate",
  TO_PAY: "amber",
  PARTLY_PAID: "red",
  PAID: "green",
  OVERPAID: "purple",
  NOTHING_TO_PAY: "slate",
  // Overview report rows
  AVAILABLE: "green",
  NOT_AVAILABLE: "slate",
  ACTION_NEEDED: "red",
  ALL_CLEAR: "green",
  NOT_RUN: "slate",
};
export const toneOf = (status) => STATUS_TONES[upper(status)] || "slate";

// Report rows carry no status_label: the guide's words.
export const REPORT_STATUS_LABELS = {
  AVAILABLE: "Available",
  NOT_AVAILABLE: "Not available",
  TO_PAY: "To pay",
  PARTLY_PAID: "Short",
  NOTHING_TO_PAY: "Nothing to pay",
  PAID: "Paid",
  ACTION_NEEDED: "Action needed",
  ALL_CLEAR: "All clear",
  NOT_RUN: "Not checked yet",
};

const saveBlob = (blob, fileName) => {
  const href = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = href;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(href), 1000);
};

// The list's download menu says excel / pdf / csv; the server wants xlsx.
const FILE_FORMATS = { excel: "xlsx", xlsx: "xlsx", pdf: "pdf", csv: "csv" };

// Same query as the view plus format=; saves the file the server makes.
const download = async ({ domain, path, query, format, fallbackName }) => {
  const fileFormat = FILE_FORMATS[format] || "xlsx";
  const { blob, fileName } = await ptRequest({ domain, path, query: { ...query, format: fileFormat }, responseType: "blob" });
  saveBlob(blob, fileName || `${fallbackName}.${fileFormat}`);
};

// ---- Header: months and departments ----

// GET reports/months/: finalised months, latest first.
export const fetchPtReportMonths = async ({ domain, companyId, signal }) => {
  const data = await ptRequest({ domain, path: "reports/months/", query: { company_id: companyId }, signal });
  const rows = Array.isArray(data) ? data : data?.results || data?.months || [];
  return list(rows)
    .map((row) => ({ value: text(row.month), label: text(row.label) || text(row.month) }))
    .filter((row) => row.value);
};

// Department filter options: the app's departments (the PT APIs take a
// department_id but have no list of their own).
export const fetchDepartmentOptions = async ({ domain }) => {
  const result = await customGetAPIQuiet(`${domain}department/`);
  if (!result?.ok) return [];
  const records = Array.isArray(result.responseDetails) ? result.responseDetails : result.responseDetails?.results || [];
  return records
    .map((row) => ({ value: String(row.id ?? row.department_id ?? ""), label: text(row.department ?? row.department_name ?? row.name) }))
    .filter((row) => row.value && row.label)
    .sort((a, b) => a.label.localeCompare(b.label));
};

const filterQuery = ({ companyId, month, siteId, departmentId }) => ({ company_id: companyId, month, site_id: siteId, department_id: departmentId });

// ---- Overview (PT3) ----

export const fetchPtOverview = async ({ domain, signal, ...filters }) => {
  const data = await ptRequest({ domain, path: "reports/overview/", query: filterQuery(filters), signal });
  if (!data || typeof data !== "object") throw new PtApiError("The server returned an invalid PT overview.");
  const cards = data.cards || {};
  return {
    month_label: text(data.month_label),
    payroll: { status: text(data.payroll?.status), label: text(data.payroll?.label) },
    cards: {
      pt_deducted: amount(cards.pt_deducted) ?? 0,
      employees_paying: cards.employees_paying ?? 0,
      exempt: cards.exempt ?? 0,
      to_pay_now: amount(cards.to_pay_now) ?? 0,
    },
    note: text(data.note),
    reports: list(data.reports).map((row) => ({
      key: text(row.key),
      site: text(row.site),
      details: text(row.details),
      status: upper(row.status),
      period_id: row.period_id ?? null,
    })),
    by_site: list(data.by_site).map((row) => ({
      id: row.site?.id ?? text(row.site?.name),
      site: text(row.site?.name),
      how_collected: text(row.how_collected),
      paying: row.paying ?? 0,
      deducted_this_month: amount(row.deducted_this_month),
      period_so_far: amount(row.period_so_far),
      payment_due_label: text(row.payment_due_label),
      status: upper(row.status),
      status_label: text(row.status_label),
      period_id: row.period_id ?? null,
    })),
    total: { paying: data.total?.paying ?? 0, deducted_this_month: amount(data.total?.deducted_this_month) ?? 0 },
  };
};

export const downloadPtOverview = ({ domain, format, ...filters }) =>
  download({ domain, path: "reports/overview/", query: filterQuery(filters), format, fallbackName: `PT_Overview_${filters.month}` });

// ---- PT Register (PT4) and PT Calculation drawer (PT4a) ----

const fromApiRegisterRow = (row) => {
  const status = upper(row.status);
  const pt = amount(row.pt_this_month);
  return {
    id: row.employee_id,
    emp_id: text(row.employee_code),
    name: text(row.name),
    department: text(row.department),
    site_name: text(row.site?.name),
    how_collected: text(row.how_collected),
    monthly_wage: amount(row.monthly_wage),
    pt,
    collected: amount(row.collected_this_period),
    status,
    // A negative month total reads "Refund" (guide PT4).
    status_label: pt != null && pt < 0 ? "Refund" : text(row.status_label) || status,
    status_tone: pt != null && pt < 0 ? "purple" : toneOf(status),
    has_settlement: Boolean(row.has_settlement),
  };
};

const registerQuery = ({ status, search, page, pageSize, ...filters }) => ({ ...filterQuery(filters), status, search, page, page_size: pageSize });

export const fetchPtRegister = async ({ domain, signal, ...filters }) => {
  const data = await ptRequest({ domain, path: "reports/register/", query: registerQuery(filters), signal });
  if (!data || typeof data !== "object") throw new PtApiError("The server returned an invalid PT register.");
  const rows = list(data.results).map(fromApiRegisterRow);
  return {
    month_label: text(data.month_label),
    count: data.count ?? rows.length,
    rows,
    total_text: text(data.totals?.text),
    note: text(data.note),
  };
};

export const downloadPtRegister = ({ domain, format, ...filters }) =>
  download({
    domain,
    path: "reports/register/",
    query: registerQuery({ ...filters, page: undefined, pageSize: undefined }),
    format,
    fallbackName: `PT_Register_${filters.month}`,
  });

// GET reports/register/<employee_id>/?month= — This Month tab.
export const fetchPtCalculation = async ({ domain, companyId, employeeId, month, signal }) => {
  const data = await ptRequest({ domain, path: `reports/register/${employeeId}/`, query: { company_id: companyId, month }, signal });
  if (!data?.employee) throw new PtApiError("The server returned an invalid PT calculation.");
  const status = upper(data.status);
  return {
    employee: { id: data.employee.id, code: text(data.employee.code), name: text(data.employee.name) },
    month_label: text(data.month_label),
    status,
    status_label: text(data.status_label) || status,
    status_tone: toneOf(status),
    rule_used: {
      site: text(data.rule_used?.site),
      site_note: text(data.rule_used?.site_note),
      period: text(data.rule_used?.period),
      how_collected: text(data.rule_used?.how_collected),
    },
    wage: { monthly: text(data.wage?.monthly), note: text(data.wage?.note) },
    lines: (Array.isArray(data.pt_for_this_month) ? data.pt_for_this_month : []).map(text).filter(Boolean),
    parts: list(data.parts).map((part) => ({
      part: upper(part.part),
      site: text(part.site),
      period_label: text(part.period_label),
      amount: amount(part.amount),
      status_label: text(part.status_label),
      settle_label: text(part.settle_label),
    })),
    pt_this_month: amount(data.pt_this_month),
    payslip: text(data.payslip),
    warnings: (Array.isArray(data.warnings) ? data.warnings : []).map((item) => (typeof item === "object" ? text(item.message || item.code) : text(item))).filter(Boolean),
  };
};

// GET reports/register/<employee_id>/history/ — every finalised month.
export const fetchPtCalculationHistory = async ({ domain, companyId, employeeId, signal }) => {
  const data = await ptRequest({ domain, path: `reports/register/${employeeId}/history/`, query: { company_id: companyId }, signal });
  return list(data?.history).map((row) => ({
    id: row.id ?? `${row.month}-${row.part}`,
    label: text(row.label || row.month),
    site: text(row.site?.name),
    period_label: text(row.period_label),
    monthly_wage: amount(row.monthly_wage),
    pt_amount: amount(row.pt_amount),
    status_label: text(row.status_label),
    status_tone: toneOf(row.status),
    settlement: upper(row.part) === "SETTLEMENT",
  }));
};

// ---- Payments (PT5) and Record PT Payment (PT5a) ----

const fromApiPeriod = (row) => {
  const status = upper(row.status);
  return {
    id: row.id,
    period_key: text(row.period_key),
    period_label: text(row.period_label),
    site_id: row.site?.id ?? null,
    site: text(row.site?.name),
    state: text(row.state),
    registration_no: text(row.registration_no),
    employees: row.employees ?? 0,
    payable: amount(row.payable) ?? 0,
    payable_label: text(row.payable_label),
    paid: amount(row.paid) ?? 0,
    balance: amount(row.balance) ?? 0,
    paid_on: text(row.last_paid_on),
    due: text(row.due_date),
    status,
    status_label: text(row.status_label) || status,
    status_tone: toneOf(status),
    overdue: Boolean(row.overdue),
    action: upper(row.action) === "RECORD_PAYMENT" ? "RECORD_PAYMENT" : "VIEW",
  };
};

const fromApiPayment = (payment) => ({
  id: payment.id,
  type: upper(payment.type),
  type_label: text(payment.type_label) || text(payment.type),
  paid_on: text(payment.paid_on),
  signed_amount: amount(payment.signed_amount ?? payment.amount) ?? 0,
  reference: text(payment.reference),
  remarks: text(payment.remarks),
  reversed: Boolean(payment.reversed),
  recorded_by: text(payment.recorded_by),
  recorded_at: text(payment.recorded_at),
  can_reverse: Boolean(payment.can_reverse),
});

const paymentsQuery = ({ companyId, period, from, to, siteId, search }) => ({
  company_id: companyId,
  period,
  from: period === "custom" ? from : undefined,
  to: period === "custom" ? to : undefined,
  site_id: siteId,
  search,
});

export const fetchPtPayments = async ({ domain, signal, ...filters }) => {
  const data = await ptRequest({ domain, path: "payments/", query: paymentsQuery(filters), signal });
  if (!data || typeof data !== "object") throw new PtApiError("The server returned an invalid PT payments list.");
  const cards = data.cards || {};
  const rows = list(data.results).map(fromApiPeriod);
  return {
    range: { from: text(data.range?.from), to: text(data.range?.to) },
    cards: {
      to_pay_now: amount(cards.to_pay_now) ?? 0,
      short_paid: amount(cards.short_paid) ?? 0,
      paid_last_2_periods: amount(cards.paid_last_2_periods) ?? 0,
      collecting_or_not_due: cards.collecting_or_not_due ?? 0,
    },
    count: data.count ?? rows.length,
    rows,
  };
};

export const downloadPtPayments = ({ domain, format, ...filters }) =>
  download({ domain, path: "payments/", query: paymentsQuery(filters), format, fallbackName: "PT_Payments" });

// GET payments/<period_id>/ — the period drawer and the record drawer header.
export const fetchPtPaymentPeriod = async ({ domain, companyId, periodId, signal }) => {
  const data = await ptRequest({ domain, path: `payments/${periodId}/`, query: { company_id: companyId }, signal });
  if (!data?.id) throw new PtApiError("The server returned an invalid payment period.");
  return {
    ...fromApiPeriod(data),
    title: text(data.title) || `${text(data.site?.name)} · ${text(data.period_label)}`,
    period_type_label: text(data.period_type_label),
    local_body: text(data.local_body),
    months: data.months ?? null,
    months_finalised: data.months_finalised ?? null,
    is_complete: data.is_complete !== false,
    collect_mode_label: text(data.collect_mode_label),
    version_name: text(data.version?.name),
    can_record: Boolean(data.can_record),
    record_blocked_reason: text(data.record_blocked_reason),
    next_type: upper(data.next_type) === "BALANCE" ? "BALANCE" : "FIRST",
    start_month: text(data.start_month),
    payments: list(data.payments).map(fromApiPayment),
  };
};

// GET payments/<period_id>/statement/ — Company PT statement (view).
export const fetchPtStatement = async ({ domain, companyId, periodId, signal }) => {
  const data = await ptRequest({ domain, path: `payments/${periodId}/statement/`, query: { company_id: companyId }, signal });
  const header = data?.header || {};
  return {
    header: {
      company: text(header.company),
      site: text(header.site?.name),
      state: text(header.state),
      local_body: text(header.local_body),
      registration_no: text(header.registration_no),
      period: text(header.period?.label),
      version: text(header.version?.name),
      note: text(header.note),
    },
    rows: list(data?.rows).map((row) => ({
      id: row.employee_id ?? row.employee_code,
      emp_id: text(row.employee_code),
      name: text(row.name),
      months_label: text(row.months_label),
      pt_wage: amount(row.pt_wage),
      pt: amount(row.pt),
      parts_label: text(row.parts_label),
    })),
    totals: {
      employees: data?.totals?.employees ?? 0,
      pt_wage: amount(data?.totals?.pt_wage),
      pt: amount(data?.totals?.pt) ?? 0,
      paid: amount(data?.totals?.paid) ?? 0,
      balance: amount(data?.totals?.balance) ?? 0,
    },
  };
};

export const downloadPtStatement = ({ domain, companyId, periodId, format }) =>
  download({ domain, path: `payments/${periodId}/statement/`, query: { company_id: companyId }, format, fallbackName: `PT_Statement_${periodId}` });

// POST payments/<period_id>/status-preview/ — live line under the amount.
export const previewPtPayment = async ({ domain, companyId, periodId, amount: paid, signal }) => {
  const data = await ptRequest({ domain, path: `payments/${periodId}/status-preview/`, method: "POST", body: { company_id: companyId, amount: String(paid) }, signal });
  return { status: upper(data?.status), line: text(data?.line).trim(), status_label: text(data?.status_label) };
};

// Server field -> drawer field (record / reverse).
const withFields = (error) => {
  if (error instanceof PtApiError && error.fieldErrors) error.fields = { ...error.fieldErrors };
  return error;
};

// POST payments/<period_id>/record/ -> { message, period, payment }.
export const recordPtPayment = async ({ domain, companyId, periodId, form }) => {
  try {
    const data = await ptRequest({
      domain,
      path: `payments/${periodId}/record/`,
      method: "POST",
      body: {
        company_id: companyId,
        type: form.type,
        paid_on: form.paid_on,
        amount: String(form.amount).trim(),
        reference: form.reference.trim(),
        ...(form.remarks.trim() ? { remarks: form.remarks.trim() } : {}),
      },
    });
    return { message: text(data?.message), period: data?.data?.period ? fromApiPeriod(data.data.period) : null };
  } catch (error) {
    throw withFields(error);
  }
};

// POST payments/<payment_id>/reverse/ (payment id, not period id).
export const reversePtPayment = async ({ domain, companyId, paymentId, reason }) => {
  try {
    const data = await ptRequest({ domain, path: `payments/${paymentId}/reverse/`, method: "POST", body: { company_id: companyId, reason: reason.trim() } });
    return { message: text(data?.message), period: data?.data?.period ? fromApiPeriod(data.data.period) : null };
  } catch (error) {
    throw withFields(error);
  }
};

// ---- Validation (PT6) and Validation Issue drawer (PT6a) ----

export const SEVERITY_WORDS = { BLOCKING: "Blocking", WARNING: "Warning", INFO: "Info" };
export const SEVERITY_COLORS = { BLOCKING: "#DC2626", WARNING: "#F0954A", INFO: "#2563EB" };

const fromApiIssue = (row) => {
  const severity = upper(row.severity);
  return {
    id: row.id,
    rule_code: upper(row.rule_code),
    is_employee: Boolean(row.employee),
    name: text(row.name_or_site),
    emp_id: text(row.employee_code),
    site: text(row.pt_site?.name || row.site?.name),
    issue: text(row.issue),
    severity,
    severity_label: SEVERITY_WORDS[severity] || text(row.severity),
    impact: text(row.impact),
    fix_in: text(row.fix_in),
    fix_target: upper(row.fix_target),
  };
};

export const fetchPtValidation = async ({ domain, companyId, month, siteId, severity, search, signal }) => {
  const data = await ptRequest({ domain, path: "validation/", query: { company_id: companyId, month, site_id: siteId, severity, search }, signal });
  if (!data || typeof data !== "object") throw new PtApiError("The server returned an invalid validation list.");
  const cards = data.cards || {};
  return {
    month: text(data.month),
    month_label: text(data.month_label),
    last_run_label: text(data.last_run?.label),
    cards: { all: cards.all ?? 0, blocking: cards.blocking ?? 0, warning: cards.warning ?? 0, info: cards.info ?? 0 },
    issues: list(data.issues).map(fromApiIssue),
  };
};

export const recheckPtValidation = async ({ domain, companyId, month }) => {
  const data = await ptRequest({ domain, path: "validation/recheck/", method: "POST", body: { company_id: companyId, month } });
  return { message: text(data?.message), last_run_label: text(data?.data?.last_run?.label) };
};

export const fetchPtIssue = async ({ domain, companyId, issueId, signal }) => {
  const data = await ptRequest({ domain, path: `validation/issues/${issueId}/`, query: { company_id: companyId }, signal });
  if (!data?.id) throw new PtApiError("The server returned an invalid validation issue.");
  const severity = upper(data.header?.severity || data.severity);
  return {
    ...fromApiIssue(data),
    title: text(data.header?.title || data.name_or_site),
    month_label: text(data.header?.month_label),
    severity,
    severity_label: SEVERITY_WORDS[severity] || severity,
    problem: text(data.problem || data.issue),
    effect: text(data.effect),
    steps: list(data.fix_steps).map((step, index) => ({ step: step.step ?? index + 1, title: text(step.title), detail: text(step.detail) })),
    actions: list(data.actions).map((action) => ({
      action: upper(action.action),
      label: text(action.label),
      fix_target: upper(action.fix_target),
      enabled: action.enabled !== false,
      note: text(action.note),
    })),
    holds_employee_payroll: Boolean(data.holds_employee_payroll),
    links: {
      employee_id: data.links?.employee_id ?? null,
      site_id: data.links?.site_id ?? null,
      period_id: data.links?.period_id ?? null,
      month: text(data.links?.month),
    },
  };
};
